import { useFormikContext } from "formik";
import { queryCache } from "react-query";

import { GET_DEAL_SETUP, DEAL_QUERY_KEYS } from "manager/hooks/api";
import { APPLICANT_TYPES, NOTIFICATIONS } from "shared/config/constants";
import api from "shared/lib/api";
import { useVeroFlag } from "shared/utils/featureFlags";
import { notifySuccess, openNotification } from "shared/utils/ui";
import { VeroFlag } from "shared/utils/veroFlags";

// Runway implies configurable quantities. Mirrors GrowthBookClient —
// don't check the two flags independently.
export const useDocQuantitiesUiEnabled = () => {
  const configurableEnabled = useVeroFlag(
    VeroFlag.useConfigurableDocQuantities
  );
  const runwayEnabled = useVeroFlag(VeroFlag.useDocumentRunwayCalculation);
  return configurableEnabled || runwayEnabled;
};

export const buildApplicantTypes = (toggleFields) => [
  {
    key: "applicant",
    label: "Applicant",
    toggleField: toggleFields.applicant,
    applicantType: APPLICANT_TYPES.applicant,
  },
  {
    key: "occupant",
    label: "Occupant (18+)",
    toggleField: toggleFields.occupant,
    applicantType: APPLICANT_TYPES.occupant,
  },
  {
    key: "guarantor",
    label: "Guarantor",
    toggleField: toggleFields.guarantor,
    applicantType: APPLICANT_TYPES.guarantor,
  },
];

// Refetches without clearing cache so isLoading stays false and the
// Skeleton overlay doesn't trigger. Returns a promise so callers can hold
// optimistic UI until refetch completes.
const backgroundRefreshDealSetup = async () => {
  await Promise.all([
    queryCache.refetchQueries(GET_DEAL_SETUP),
    queryCache.refetchQueries(DEAL_QUERY_KEYS.getDefaultDealSetup()),
  ]);
};

// Single source of truth for which AR fields the change handlers touch
// and which must survive form refetches.
export const AR_EDITABLE_FIELDS = Object.freeze([
  "isActive",
  "requiredQty",
  "runwayMonths",
]);

export const getAttachmentsFilter =
  (attachmentType) => (values, applicantType) =>
    values.requiredAttachments
      ? values.requiredAttachments
          ?.map((attachment, index) => ({
            id: attachment.id,
            label: attachment.name,
            displayName: attachment.displayName,
            displayNameTemplate: attachment.displayNameTemplate,
            checked: values?.requiredAttachments[index]?.isActive,
            applicantType: attachment.applicantType,
            isDefault: attachment.isDefault,
            attachmentType: attachment.attachmentType,
            requiredQty: attachment.requiredQty,
            runwayMonths: attachment.runwayMonths,
          }))
          ?.filter(
            (item) =>
              item.applicantType === applicantType &&
              item.attachmentType === attachmentType
          )
      : [];

const getAssetsVerificationCommon = (
  isArgyleEnabled,
  values,
  fieldName,
  disabled,
  label,
  id,
  childLabel,
  childExtraInfo
) => {
  const children = [];
  if (isArgyleEnabled) {
    children.push({
      id: fieldName,
      label: childLabel,
      checked: values[fieldName],
      extraInfo: childExtraInfo,
    });
  }

  return {
    label,
    id,
    disabled,
    children,
  };
};

export const getAssetsVerificationItem = (
  isArgyleEnabled,
  values,
  fieldName,
  disabled
) => {
  const label = isArgyleEnabled ? "Balances Verification" : "Balances";
  const id = isArgyleEnabled ? "balances-verification" : "balances";
  const childLabel = "Perform Balances Verification";
  const childExtraInfo = `If enabled and the user links a current company automatically,
      VERO will attempt to collect and confirm the balances from the
      future renter if they choose to connect their bank account during
      the application flow.`;
  return getAssetsVerificationCommon(
    isArgyleEnabled,
    values,
    fieldName,
    disabled,
    label,
    id,
    childLabel,
    childExtraInfo
  );
};

export const getPrescreeningAssetsVerificationItem = (
  isArgyleEnabled,
  values,
  fieldName,
  disabled
) => {
  const label = "Balances";
  const id = "balances-verification";
  const childLabel = "Always allow lead to perform Balance Verification";
  const childExtraInfo = `If checked lead will always be offered to perform a balance verification regardless if
      they connect to Lynk.`;
  return getAssetsVerificationCommon(
    isArgyleEnabled,
    values,
    fieldName,
    disabled,
    label,
    id,
    childLabel,
    childExtraInfo
  );
};

export const getBlockApplicationSubmitIfNoIncomeItem = (
  values,
  prop,
  isGuarantor
) => {
  const userLabel = isGuarantor ? "guarantor" : "applicant/occupant";
  return {
    id: prop,
    label: "Block Application Submission",
    checked: values[prop],
    extraInfo: `To submit applications, ${userLabel} must verify their income or specify a reason why unable. Managers with special permission can unblock the ${userLabel} if needed.`,
  };
};

export const getForceIncomeDocumentsItem = ({
  values,
  prop,
  isGuarantor,
  disabled,
}) => {
  const userLabel = isGuarantor ? "guarantor" : "applicant/occupant";
  return {
    id: prop,
    label: "Require Document Uploads",
    checked: values[prop],
    disabled,
    extraInfo: `When enabled, the ${userLabel} must upload income or international documents to complete income verification, even if any linked employment or income data has already produced verified income.`,
  };
};

export const getAllowManagersToViewSSNItem = (fieldName, values) => {
  return {
    id: fieldName,
    label: "Allow managers to view SSN / ITIN",
    checked: values[fieldName],
    extraInfo:
      "If enabled, managers with the correct permission will be able to view the entire SSN / ITIN. Managers without the permission will be able to view the last 4 digits of the SSN / ITIN.",
  };
};

export const getConditionallyProceedItem = (fieldName, values) => {
  return {
    id: fieldName,
    label: "Hide Criminal Records Until Conditionally Proceeded",
    checked: values[fieldName],
    extraInfo:
      "Leasing agents will be required to conditionally proceed with the applicant in order to view their criminal records. This applies to all applicant types on the application.",
  };
};

// Tuples not plain objects — Object.entries iterates integer-like keys
// ("1099") first, which would override insertion order.
const DOMESTIC_DOC_TYPE_ENTRIES = [
  ["paystub", "Paystubs"],
  ["w-2", "W-2"],
  ["w2", "W-2"],
  ["bank-statement", "Bank Statements"],
  ["bank statement", "Bank Statements"],
  ["1099", "1099s"],
];

export const INTL_DOC_TYPE_ENTRIES = [
  ["paystub", { label: "Paystubs or Payslips", type: "international-paystub" }],
  ["w2", { label: "Paystubs or Payslips", type: "international-paystub" }],
  ["w-2", { label: "Paystubs or Payslips", type: "international-paystub" }],
  ["bank-statement", { label: "Bank Statements", type: "bank-statement" }],
  ["bank statement", { label: "Bank Statements", type: "bank-statement" }],
  ["1099", { label: "Self-Employment Records", type: "international-tax-form" }],
];

const QUANTITY_PATTERN =
  /^(\d+)\s+(?:or\s+more\s+)?(?:most\s+recent\s+)?(?=[a-z])/i;

const parseDocLabel = (label, nameEntries) => {
  const lower = (label || "").toLowerCase();
  const match = lower.match(QUANTITY_PATTERN);
  const count = match ? parseInt(match[1], 10) : null;

  for (const [key, displayName] of nameEntries) {
    if (lower.includes(key)) {
      const mappedLabel = typeof displayName === "string" ? displayName : displayName.label;
      return { label: mappedLabel, count, type: typeof displayName === "object" ? displayName.type : key };
    }
  }

  return null;
};

export const getIntlDocMapping = (label) =>
  parseDocLabel(label, INTL_DOC_TYPE_ENTRIES);

export const getDomesticDocMapping = (label) =>
  parseDocLabel(label, DOMESTIC_DOC_TYPE_ENTRIES);

// First human-readable message out of a DRF error body — handles strings,
// {detail}, {field: [msg]|msg}, and arrays of either (bulk-create responses).
const extractFirstErrorMessage = (body) => {
  if (!body) return null;
  if (typeof body === "string") return body;
  if (Array.isArray(body)) {
    for (const entry of body) {
      const msg = extractFirstErrorMessage(entry);
      if (msg) return msg;
    }
    return null;
  }
  if (body.detail) return body.detail;
  const firstMsg = Object.values(body)
    .map((v) => {
      if (Array.isArray(v) && typeof v[0] === "string") return v[0];
      if (typeof v === "string") return v;
      return null;
    })
    .find(Boolean);
  return firstMsg || null;
};

// Wraps a Formik setFieldValue on an income-section toggle (Argyle, Plaid)
// with a guard that blocks the user from turning the LAST income surface
// off while docs are still configured for the affected persona(s). Returns
// a function to drop into the toggle's onChange.
//
// When the guard trips, we surface a warning notification and revert the
// toggle. The rule is symmetric with the IncomeSurfaceGate — both
// directions are enforced in the frontend only. Gated on the consolidation
// flag so legacy companies keep their pre-PRODUCT-1005 toggle behavior.
export const useGuardedIncomeToggle = ({
  toggleField,
  siblingIncomeField,
  personaScope,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const consolidationOn = useDocQuantitiesUiEnabled();
  const docsConfigured = useIncomeDocConfiguredByPersona();

  // Returns true when the toggle was applied, false when blocked. Callers
  // use the return value to gate follow-up side effects (e.g. clearing child
  // fields on toggle-off) so a blocked guard doesn't silently wipe children
  // while the parent toggle remains on.
  return (checked) => {
    // Turning ON or flag-off — no guard needed.
    if (checked || !consolidationOn) {
      setFieldValue(toggleField, checked);
      return true;
    }

    // Blocking only fires when the sibling surface is already off — i.e.
    // this toggle being turned off would leave the persona without any
    // income surface.
    const siblingOn = Boolean(values?.[siblingIncomeField]);
    if (siblingOn) {
      setFieldValue(toggleField, checked);
      return true;
    }

    // VERO GO intentionally has no income surface. The toggle is already
    // locked in the UI in that mode (VERO GO enforcement), so this should
    // never fire, but guard defensively.
    const veroGoActive = Boolean(
      values?.isVeroGoActive ?? values?.veroGoActive
    );
    if (veroGoActive) {
      setFieldValue(toggleField, checked);
      return true;
    }

    const affectedPersonas =
      personaScope === "guarantor"
        ? ["guarantor"]
        : ["applicant", "occupant"];

    const anyDocsActive = affectedPersonas.some((p) => docsConfigured[p]);

    if (!anyDocsActive) {
      setFieldValue(toggleField, checked);
      return true;
    }

    openNotification(
      "Remove the configured income documents under Application Setup before turning off the last income verification surface — documents can't render without one.",
      NOTIFICATIONS.warning
    );
    // No-op: leave the toggle at its previous value.
    return false;
  };
};

let firedThisTickForceDocsMessages = new Set();
const fireForceDocsNotification = (message) => {
  if (firedThisTickForceDocsMessages.has(message)) return;
  firedThisTickForceDocsMessages.add(message);
  openNotification(message, NOTIFICATIONS.warning);
  setTimeout(() => {
    firedThisTickForceDocsMessages.delete(message);
  }, 0);
};

// Disable-side guard for the income-docs grids. International is intentionally
// out of scope: only flipping OFF the income docs surface (financial /
// unverified) for a persona with force-docs ON should block. International
// docs can be toggled freely without tripping this guard.
export const useGuardedIncomeDocsToggle = ({ surfaceFamily }) => {
  const { values, setFieldValue } = useFormikContext();
  return (field, value) => {
    if (value) {
      setFieldValue(field, value);
      return true;
    }
    // Intl tab toggles never trip the guard — short-circuit here.
    if (surfaceFamily !== "income") {
      setFieldValue(field, value);
      return true;
    }
    const FIELD_TO_PERSONA = {
      requiresApplicantAdditionalAttachementsForIncome: "applicant",
      requiresOccupantAdditionalAttachementsForIncome: "occupant",
      requiresGuarantorAdditionalAttachementsForIncome: "guarantor",
    };
    const personaKey = FIELD_TO_PERSONA[field];
    if (!personaKey) {
      setFieldValue(field, value);
      return true;
    }
    const FORCE_DOCS_FIELDS = {
      applicant: "forceApplicantIncomeDocuments",
      occupant: "forceOccupantIncomeDocuments",
      guarantor: "forceGuarantorIncomeDocuments",
    };
    const personasInScope =
      personaKey === "applicant"
        ? ["applicant", "occupant"]
        : [personaKey];
    const wouldStrandAnyPersona = personasInScope.some(
      (key) => Boolean(values?.[FORCE_DOCS_FIELDS[key]])
    );
    if (wouldStrandAnyPersona) {
      fireForceDocsNotification(
        "Turn off Require Document Uploads under Screening Setup before disabling income documents."
      );
      return false;
    }
    setFieldValue(field, value);
    return true;
  };
};

export const useHasIncomeDocsSurfaceByPersona = () => {
  const { values } = useFormikContext();
  const consolidationOn = useDocQuantitiesUiEnabled();
  const ocrAnyOn =
    Boolean(values?.enableIncomeFromOcrPaystub) ||
    Boolean(values?.enableIncomeFromOcr1099) ||
    Boolean(values?.enableIncomeFromOcrBankStatement);
  const personaHas = (sectionField) => {
    if (consolidationOn) return Boolean(values?.[sectionField]);
    return Boolean(values?.[sectionField]) || ocrAnyOn;
  };
  return {
    applicant: personaHas("requiresApplicantAdditionalAttachementsForIncome"),
    occupant: personaHas("requiresOccupantAdditionalAttachementsForIncome"),
    guarantor: personaHas("requiresGuarantorAdditionalAttachementsForIncome"),
  };
};

export const useGuardedForceDocsToggle = ({ hasDocSurface }) => {
  const { setFieldValue } = useFormikContext();
  return (field, value) => {
    if (value && !hasDocSurface) {
      fireForceDocsNotification(
        "Enable income documents under Application Setup before requiring document uploads."
      );
      return false;
    }
    setFieldValue(field, value);
    return true;
  };
};


// Returns which personas have ANY income doc configured right now. Used by
// the Screening Setup income toggles to decide whether flipping the toggle
// off is safe (no docs) or needs to be blocked (docs would be stranded).
//
// "Configured" means any Unverified_Income or International_Documents AR
// row is is_active=True for that persona, OR any OCR toggle is on (OCR
// toggles are deal-setup-wide so they block turning off income for any
// persona).
export const useIncomeDocConfiguredByPersona = () => {
  const { values } = useFormikContext();

  // Under consolidation, AR rows (is_active) are the authoritative signal
  // for whether a persona has docs configured — OCR toggles are a mirrored
  // shadow that gets recomputed from rows on save. Checking them here
  // would return stale state: the user can deactivate every row in Formik,
  // but OCR toggles stay True until the save flushes. That would keep the
  // income-off guard firing even after docs are effectively off.
  //
  // AR.applicantType comes through the API as an integer (IntegerChoice
  // on the backend): 1=applicant, 2=guarantor, 3=occupant. See
  // shared/config/constants/app.constants.ts → APPLICANT_TYPES.
  const personaHasActiveRow = (applicantTypeInt) =>
    (values?.requiredAttachments || []).some((ar) => {
      if (!ar?.isActive) return false;
      if (ar.applicantType !== applicantTypeInt) return false;
      return (
        ar.attachmentType === "UI" /* Unverified_Income */
          || ar.attachmentType === "ID" /* International_Documents */
      );
    });

  return {
    applicant: personaHasActiveRow(APPLICANT_TYPES.applicant),
    occupant: personaHasActiveRow(APPLICANT_TYPES.occupant),
    guarantor: personaHasActiveRow(APPLICANT_TYPES.guarantor),
  };
};

// Mirror of the backend doc-enable validator: managers can only configure
// income docs for a persona that has an income surface active (Argyle or
// Plaid) under Screening Setup. VERO GO skips this — docs are its entire
// income surface by design. Flag-off companies skip too; the validator is
// only in effect alongside the rest of consolidation.
//
// Returns { blocked, reason } per persona key so the grid can disable rows
// with an explanation instead of letting the user save and hit a 400.
export const useIncomeSurfaceByPersona = () => {
  const { values } = useFormikContext();
  const consolidationOn = useDocQuantitiesUiEnabled();
  const veroGoActive = Boolean(
    values?.isVeroGoActive ?? values?.veroGoActive
  );

  if (!consolidationOn || veroGoActive) {
    return {
      applicant: { blocked: false, reason: null },
      occupant: { blocked: false, reason: null },
      guarantor: { blocked: false, reason: null },
    };
  }

  const applicantOn =
    Boolean(values?.requiresApplicantOccupantArgyleVerification) ||
    Boolean(values?.requiresIncomeAssetApplicantOccupant);
  const guarantorOn =
    Boolean(values?.requiresGuarantorArgyleVerification) ||
    Boolean(values?.requiresIncomeAssetGuarantors);

  const reasonApplicant = applicantOn
    ? null
    : "Enable Income or Balances Verification for applicants and occupants under Screening Setup before configuring these documents.";
  const reasonGuarantor = guarantorOn
    ? null
    : "Enable Income or Balances Verification for guarantors under Screening Setup before configuring these documents.";

  return {
    applicant: { blocked: !applicantOn, reason: reasonApplicant },
    occupant: { blocked: !applicantOn, reason: reasonApplicant },
    guarantor: { blocked: !guarantorOn, reason: reasonGuarantor },
  };
};

// Shared attachment quantity/toggle handlers used by all four attachment sections.
export const useAttachmentHandlers = ({ attachmentType, personas } = {}) => {
  const { values, setFieldValue } = useFormikContext();

  const onChangeAttachment = (id, checked) => {
    const index = values.requiredAttachments?.findIndex(
      (item) => item.id === id
    );
    if (index >= 0) {
      setFieldValue(`requiredAttachments[${index}].isActive`, checked);
    }
  };

  const onChangeQuantity = (id, quantity) => {
    const index = values.requiredAttachments?.findIndex(
      (item) => item.id === id
    );
    if (index >= 0) {
      setFieldValue(`requiredAttachments[${index}].requiredQty`, quantity);
    }
  };

  const onChangeRunway = (id, months) => {
    const index = values.requiredAttachments?.findIndex(
      (item) => item.id === id
    );
    if (index >= 0) {
      setFieldValue(`requiredAttachments[${index}].runwayMonths`, months);
    }
  };

  // `silent` suppresses the per-call success toast so bulk operations
  // (e.g. reset-to-applicant) can fire a single summary toast instead.
  const handleAddCustomDoc = async (name, applicantTypes, { silent = false } = {}) => {
    if (!attachmentType || !personas) return;
    const targetPersonas = applicantTypes
      ? personas.filter((p) => applicantTypes.includes(p.applicantType))
      : personas;
    const attachments = targetPersonas.map((p) => ({
      name,
      objectId: values.id,
      dealSetup: values.id,
      applicantType: p.applicantType,
      isActive: true,
      attachmentType,
      requiredQty: 1,
    }));
    try {
      await api.createAttachments({ attachments });
      await backgroundRefreshDealSetup();
      if (!silent) notifySuccess(`Document "${name}" added`);
    } catch (error) {
      // setupApi wraps axios errors in ApiError; body lives on .errors.
      const body = error?.errors ?? error?.response?.data;
      const message = extractFirstErrorMessage(body)
        || `Failed to add document "${name}"`;
      openNotification(message, NOTIFICATIONS.error);
      // Rethrow — callers like handleCustomize only flip state on success.
      throw error;
    }
  };

  const handleDeleteDoc = async (ids, { silent = false } = {}) => {
    const idSet = new Set(ids);
    // One doc name can span all three personas (1-3 ids → 1 title).
    const nameSet = new Set(
      (values.requiredAttachments || [])
        .filter((ar) => idSet.has(ar.id))
        .map((ar) => ar.name)
        .filter(Boolean)
    );
    const displayName =
      nameSet.size === 1 ? `Document "${Array.from(nameSet)[0]}"` : "Documents";
    try {
      await Promise.all(ids.map((id) => api.deleteAttachment({ id })));
      await backgroundRefreshDealSetup();
      if (!silent) notifySuccess(`${displayName} removed`);
    } catch (error) {
      openNotification(`Failed to remove ${displayName}`, NOTIFICATIONS.error);
      throw error;
    }
  };

  return {
    values,
    setFieldValue,
    onChangeAttachment,
    onChangeQuantity,
    onChangeRunway,
    handleAddCustomDoc,
    handleDeleteDoc,
  };
};

// Doc classification keyed by lowercase name — drives section header and
// the OCR "Verified" badge.
const DOC_CLASSIFICATION = {
  paystubs: { group: "Financial", verified: true },
  "bank statements": { group: "Financial", verified: true },
  "w-2": { group: "Financial", verified: true },
  "1099s": { group: "Financial", verified: true },
  "tax returns": { group: "Financial", verified: false },
  "paystubs or payslips": { group: "Financial", verified: true },
  "self-employment records": { group: "Financial", verified: true },
  passport: { group: "Identity", verified: false },
  "driver's license": { group: "Identity", verified: false },
  "photo id": { group: "Identity", verified: false },
  "letter of employment": { group: "General", verified: false },
  "rent payment confirmation": { group: "General", verified: false },
  "landlord reference letter": { group: "General", verified: false },
  "student enrollment confirmation": { group: "General", verified: false },
};

const GROUP_SORT_ORDER = ["Financial", "Identity", "General", "Custom"];

export const getDocClassification = (label, isDefault) => {
  if (!isDefault) return { group: "Custom", verified: false };
  return (
    DOC_CLASSIFICATION[(label || "").toLowerCase()] || {
      group: "General",
      verified: false,
    }
  );
};

// Default attachment names where quantity is always 1 and not configurable.
// These are identity/single-instance documents — you only have one passport, etc.
const SINGLE_DOCUMENT_DEFAULTS = new Set([
  "passport",
  "driver's license",
  "photo id",
  "student enrollment confirmation",
]);

// Customs are always editable; default identity docs never. Everything else
// (financial, letter of employment, etc.) is editable.
export const isQtyEditable = (item) => {
  if (!item.isDefault) return true;
  return !SINGLE_DOCUMENT_DEFAULTS.has((item.label || "").toLowerCase());
};

// Re-composes a label from displayNameTemplate so the stepper preview stays
// in sync while the user drags. Backend displayName is frozen at fetch time.
export const buildItemDisplayLabel = (item, qty) => {
  const template = item?.displayNameTemplate;
  const fallback =
    item?.genericName || item?.displayName || item?.label || item?.name || "";
  if (!template || !qty || qty <= 1) return fallback;
  return template.replace("{n}", String(qty));
};

// Strips quantity prefixes + non-alphanumerics so name variants
// ("pay stubs", "pay-stubs", "Paystubs") collapse to one key.
const normalizeForCollision = (label) =>
  (label || "")
    .toLowerCase()
    .replace(QUANTITY_PATTERN, "")
    .replace(/[^a-z0-9]/g, "");

// Suffix-match (not substring) so customs that just mention a token
// inside a longer name don't get incorrectly dropped — e.g. "EW2
// Employer Form" or "Form 10994" should NOT be treated as duplicates.
// Singular and plural tokens are both included since endsWith requires
// the exact tail.
const CANONICAL_TOKENS_FOR_COLLISION = [
  "paystub",
  "paystubs",
  "bankstatement",
  "bankstatements",
  "1099",
  "1099s",
  "w2",
  "w2s",
];

// Drops custom ARs that shadow a built-in financial type — defaults win.
const suppressCollidedCustoms = (items) =>
  items.filter((item) => {
    if (item.isDefault !== false) return true;
    if (getDomesticDocMapping(item.label) || getIntlDocMapping(item.label)) {
      return false;
    }
    const normalized = normalizeForCollision(item.label);
    return !CANONICAL_TOKENS_FOR_COLLISION.some((token) =>
      normalized.endsWith(token)
    );
  });

// Custom group sorts by ID ascending so newest customs land at the bottom.
const sortByGroup = (items) =>
  [...items].sort((a, b) => {
    const aIdx = GROUP_SORT_ORDER.indexOf(a.group || "Custom");
    const bIdx = GROUP_SORT_ORDER.indexOf(b.group || "Custom");
    if (aIdx !== bIdx) return aIdx - bIdx;
    if (a.group === "Custom" && b.group === "Custom") {
      return Number(a.id) - Number(b.id);
    }
    return 0;
  });

// genericName: short label for matching. label: backend-composed display
// name. requiredCount: set only when qty > 1 so single-doc rows don't show
// a noisy "x1" badge.
const enrichItem = (item, cleanLabel, qty) => {
  const { group, verified } = getDocClassification(cleanLabel, item.isDefault);
  return {
    ...item,
    genericName: cleanLabel,
    label: item.displayName || cleanLabel,
    requiredCount: qty > 1 ? qty : undefined,
    editable: isQtyEditable(item),
    group,
    verified,
  };
};

// All four modern grid sections (Required, Background, Income, Intl)
// share this mapper so labels, group classification, dedup, OCR badge,
// and qty minimums are uniform.
export const mapFinancialDocLabels = (items) =>
  sortByGroup(
    suppressCollidedCustoms(items).map((item) => {
      const mapping = getDomesticDocMapping(item.label);
      const cleanLabel = mapping ? mapping.label : item.label;
      const qty = item.requiredQty ?? 1;
      return enrichItem(item, cleanLabel, qty);
    })
  );

// International Documents grid — swaps in the intl label ("Paystubs or Payslips").
export const mapIntlDocLabels = (items) =>
  sortByGroup(
    suppressCollidedCustoms(items).map((item) => {
      const mapping = getIntlDocMapping(item.label);
      const cleanLabel = mapping ? mapping.label : item.label;
      const qty = item.requiredQty ?? 1;
      return enrichItem(item, cleanLabel, qty);
    })
  );

// Legacy-view mapper used by all four legacy sections. Count is baked into
// displayName; ordering comes from AttachmentRequestListSerializer.
// Dedup is unconditional — collided custom financial docs are suppressed
// across every legacy group so behavior is uniform.
export const mapLegacyAttachmentItems = (
  items,
  { isInternational = false, showOcrTooltip = false } = {}
) => {
  return suppressCollidedCustoms(items).map((item) => {
    const displayName = item.displayName || item.label;
    const classifyKey = isInternational
      ? getIntlDocMapping(item.label)?.label || item.label
      : getDomesticDocMapping(item.label)?.label || item.label;
    const { verified } = getDocClassification(classifyKey, item.isDefault);
    const tooltipText =
      showOcrTooltip && verified
        ? isInternational
          ? "Supports country-specific OCR"
          : "Supports OCR income extraction"
        : undefined;
    return {
      ...item,
      label: displayName,
      requiredCount: undefined,
      tooltipText,
    };
  });
};
